import { WellKnownWorkloadProvider } from 'pbts/c1/api/workload_federation/v1/workload_federation'

export type WorkloadSourceField = {
    name: string
    label: string
    required: boolean
    default?: string
    placeholder?: string
    helpText?: string
    type: 'text' | 'select'
    selectOptions?: { value: string; label: string }[]
}

export type SuccessSnippet = {
    /** Language for syntax highlighting (yaml, bash, hcl) */
    language: 'yaml' | 'bash' | 'hcl' | 'text'
    /** The raw code string (for copy-to-clipboard) */
    code: string
    /** File path hint shown above the snippet (e.g., ".github/workflows/deploy.yml") */
    filename?: string
}

export type WorkloadSourcePreset = {
    wellKnownProvider: WellKnownWorkloadProvider
    displayName: string
    description: string
    /** Key into app-icons map or a Lucide icon name */
    iconKey: string
    issuerUrl: string | null
    issuerUrlDefault: string
    issuerUrlEditable: boolean
    providerDisplayName: string
    fields: WorkloadSourceField[]
    celTemplate: (values: Record<string, string>) => string
    displayNameTemplate: (values: Record<string, string>) => string
    successSnippets: (ctx: { tenantDomain: string; clientId: string }) => SuccessSnippet[]
    securityGuidance: string
}

// ---------------------------------------------------------------------------
// GitHub Actions
// ---------------------------------------------------------------------------

const GITHUB_ACTIONS_PRESET: WorkloadSourcePreset = {
    wellKnownProvider: WellKnownWorkloadProvider.GITHUB_ACTIONS,
    displayName: 'GitHub Actions',
    description: 'Authenticate GitHub Actions workflows via OIDC',
    iconKey: 'GitHub',
    issuerUrl: 'https://token.actions.githubusercontent.com',
    issuerUrlDefault: 'https://token.actions.githubusercontent.com',
    issuerUrlEditable: false,
    providerDisplayName: 'GitHub Actions',
    fields: [
        {
            name: 'org',
            label: 'Organization',
            required: true,
            placeholder: 'acme',
            helpText: 'GitHub organization name',
            type: 'text',
        },
        {
            name: 'repo',
            label: 'Repository',
            required: true,
            placeholder: 'infra',
            helpText: 'Repository name (without the org prefix)',
            type: 'text',
        },
        {
            name: 'branch',
            label: 'Branch',
            required: false,
            default: 'refs/heads/main',
            placeholder: 'refs/heads/main',
            helpText: 'Restrict to a specific branch (optional)',
            type: 'text',
        },
        {
            name: 'environment',
            label: 'Environment',
            required: false,
            placeholder: 'production',
            helpText: 'GitHub Environment name (optional, recommended for production)',
            type: 'text',
        },
    ],
    celTemplate: (v): string => {
        const parts: string[] = [`claims.repository == "${v.org}/${v.repo}"`]
        if (v.branch) {
            parts.push(`claims.ref == "${v.branch}"`)
        }
        if (v.environment) {
            parts.push(`claims.environment == "${v.environment}"`)
        }
        return parts.join(' && ')
    },
    displayNameTemplate: (v): string => {
        let name = `GitHub: ${v.org}/${v.repo}`
        if (v.branch && v.branch !== 'refs/heads/main') {
            name += ` (${v.branch})`
        }
        if (v.environment) {
            name += ` [${v.environment}]`
        }
        return name
    },
    successSnippets: (ctx): SuccessSnippet[] => [{
        language: 'yaml',
        filename: '.github/workflows/deploy.yml',
        code: `permissions:
  id-token: write
  contents: read

jobs:
  deploy:
    runs-on: ubuntu-latest
    steps:
      - name: Get ConductorOne token
        id: c1token
        uses: conductorone/oidc-token-action@v1
        with:
          audience: ${ctx.tenantDomain}
          client_id: ${ctx.clientId}`,
    }],
    securityGuidance:
        'For production workloads, enable GitHub Environments and add an environment constraint. ' +
        'This prevents any branch or workflow in the repo from authenticating -- only jobs running ' +
        'in the specified environment can. For maximum security, also pin to a specific reusable ' +
        'workflow via job_workflow_ref.',
}

// ---------------------------------------------------------------------------
// GitLab CI/CD
// ---------------------------------------------------------------------------

const GITLAB_CI_PRESET: WorkloadSourcePreset = {
    wellKnownProvider: WellKnownWorkloadProvider.GITLAB_CI,
    displayName: 'GitLab CI/CD',
    description: 'Authenticate GitLab CI/CD pipelines via OIDC',
    iconKey: 'GitLab',
    issuerUrl: null,
    issuerUrlDefault: 'https://gitlab.com',
    issuerUrlEditable: true,
    providerDisplayName: 'GitLab CI/CD',
    fields: [
        {
            name: 'projectPath',
            label: 'Project path',
            required: true,
            placeholder: 'group/project',
            helpText: 'Full project path (e.g., acme/infra)',
            type: 'text',
        },
        {
            name: 'ref',
            label: 'Ref',
            required: false,
            placeholder: 'main',
            helpText: 'Restrict to a specific branch or tag (optional)',
            type: 'text',
        },
        {
            name: 'requireProtectedRef',
            label: 'Require protected ref',
            required: false,
            default: 'true',
            helpText: 'Only allow jobs running on protected branches or tags',
            type: 'select',
            selectOptions: [
                { value: 'true', label: 'Yes (recommended)' },
                { value: 'false', label: 'No' },
            ],
        },
    ],
    celTemplate: (v): string => {
        const parts: string[] = [`claims.project_path == "${v.projectPath}"`]
        if (v.requireProtectedRef === 'true') {
            parts.push('claims.ref_protected == "true"')
        }
        if (v.ref) {
            parts.push(`claims.ref == "${v.ref}"`)
        }
        return parts.join(' && ')
    },
    displayNameTemplate: (v): string => {
        let name = `GitLab: ${v.projectPath}`
        if (v.ref) {
            name += ` (${v.ref})`
        }
        return name
    },
    successSnippets: (ctx): SuccessSnippet[] => [{
        language: 'yaml',
        filename: '.gitlab-ci.yml',
        code: `deploy:
  id_tokens:
    C1_TOKEN:
      aud: ${ctx.tenantDomain}
  script:
    - >
      C1_ACCESS_TOKEN=$(curl -s -X POST
      "https://${ctx.tenantDomain}/auth/v1/token"
      -d "grant_type=urn:ietf:params:oauth:grant-type:token-exchange"
      -d "subject_token=$C1_TOKEN"
      -d "subject_token_type=urn:ietf:params:oauth:token-type:jwt"
      -d "client_id=${ctx.clientId}")`,
    }],
    securityGuidance:
        'Keeping "Require protected ref" enabled ensures only jobs running on protected branches ' +
        'or tags can authenticate. For high-privilege access, also bind to a specific ref value. ' +
        'GitLab lets you set aud per-job via id_tokens -- your tenant domain is used as the expected audience.',
}

// ---------------------------------------------------------------------------
// HCP Terraform / Terraform Cloud
// ---------------------------------------------------------------------------

const HCP_TERRAFORM_PRESET: WorkloadSourcePreset = {
    wellKnownProvider: WellKnownWorkloadProvider.HCP_TERRAFORM,
    displayName: 'HCP Terraform',
    description: 'Authenticate Terraform Cloud/HCP runs via OIDC',
    iconKey: 'HCPTerraform',
    issuerUrl: null,
    issuerUrlDefault: 'https://app.terraform.io',
    issuerUrlEditable: true,
    providerDisplayName: 'HCP Terraform',
    fields: [
        {
            name: 'org',
            label: 'Organization',
            required: true,
            placeholder: 'acme',
            helpText: 'Terraform Cloud organization name',
            type: 'text',
        },
        {
            name: 'workspace',
            label: 'Workspace',
            required: true,
            placeholder: 'infra-prod',
            helpText: 'Terraform Cloud workspace name',
            type: 'text',
        },
        {
            name: 'runPhase',
            label: 'Run phase',
            required: false,
            default: 'apply',
            helpText: 'Restrict to a specific run phase',
            type: 'select',
            selectOptions: [
                { value: 'apply', label: 'Apply only (recommended)' },
                { value: '', label: 'Plan and apply' },
                { value: 'plan', label: 'Plan only' },
            ],
        },
    ],
    celTemplate: (v): string => {
        const parts: string[] = [
            `claims.terraform_organization == "${v.org}"`,
            `claims.terraform_workspace_name == "${v.workspace}"`,
        ]
        if (v.runPhase) {
            parts.push(`claims.terraform_run_phase == "${v.runPhase}"`)
        }
        return parts.join(' && ')
    },
    displayNameTemplate: (v): string => {
        let name = `Terraform: ${v.org}/${v.workspace}`
        if (v.runPhase) {
            name += ` (${v.runPhase})`
        }
        return name
    },
    successSnippets: (ctx): SuccessSnippet[] => [{
        language: 'hcl',
        filename: 'conductorone.tf',
        code: `variable "tfc_conductorone_audience" {
  type    = string
  default = "${ctx.tenantDomain}"
}

# Client ID for token exchange: ${ctx.clientId}`,
    }],
    securityGuidance:
        'Restricting to "Apply only" prevents plan-phase runs from obtaining credentials. ' +
        'This is the recommended default for workloads that modify infrastructure. ' +
        'If you need separate permissions for plan vs apply, create two trusts with different ' +
        'scoped roles -- one for plan (read-only) and one for apply (write).',
}

// ---------------------------------------------------------------------------
// AWS IAM Outbound Identity Federation
// ---------------------------------------------------------------------------

const AWS_IAM_OUTBOUND_PRESET: WorkloadSourcePreset = {
    wellKnownProvider: WellKnownWorkloadProvider.AWS_IAM_OUTBOUND,
    displayName: 'AWS IAM Outbound',
    description: 'Authenticate AWS workloads via IAM Outbound Identity Federation',
    iconKey: 'AWS',
    issuerUrl: null,
    issuerUrlDefault: '',
    issuerUrlEditable: true,
    providerDisplayName: 'AWS IAM Outbound',
    fields: [
        {
            name: 'accountId',
            label: 'AWS account ID',
            required: true,
            placeholder: '123456789012',
            helpText: '12-digit AWS account ID',
            type: 'text',
        },
        {
            name: 'roleArn',
            label: 'IAM role ARN',
            required: false,
            placeholder: 'arn:aws:iam::123456789012:role/ci-deployer',
            helpText: 'Exact IAM role ARN to match (optional, recommended)',
            type: 'text',
        },
        {
            name: 'orgId',
            label: 'Organization ID',
            required: false,
            placeholder: 'o-abc1234567',
            helpText: 'AWS Organization ID for multi-account boundary (optional)',
            type: 'text',
        },
        {
            name: 'envTag',
            label: 'Environment tag',
            required: false,
            placeholder: 'production',
            helpText: 'Value of principal_tags.environment (optional)',
            type: 'text',
        },
        {
            name: 'teamTag',
            label: 'Team tag',
            required: false,
            placeholder: 'platform',
            helpText: 'Value of principal_tags.team (optional)',
            type: 'text',
        },
    ],
    celTemplate: (v): string => {
        const ns = 'claims["https://sts.amazonaws.com/"]'
        const parts: string[] = [`${ns}.aws_account == "${v.accountId}"`]
        if (v.roleArn) {
            parts.push(`claims.sub == "${v.roleArn}"`)
        }
        if (v.orgId) {
            parts.push(`${ns}.org_id == "${v.orgId}"`)
        }
        if (v.envTag) {
            parts.push(`${ns}.principal_tags.environment == "${v.envTag}"`)
        }
        if (v.teamTag) {
            parts.push(`${ns}.principal_tags.team == "${v.teamTag}"`)
        }
        return parts.join(' && ')
    },
    displayNameTemplate: (v): string => {
        if (v.roleArn) {
            const roleName = v.roleArn.split('/').pop() ?? v.roleArn
            return `AWS: ${v.accountId}/${roleName}`
        }
        return `AWS: ${v.accountId}`
    },
    successSnippets: (ctx): SuccessSnippet[] => [
        {
            language: 'bash',
            filename: 'get-token.sh',
            code: `TOKEN=$(aws sts get-web-identity-token \\
  --audience "${ctx.tenantDomain}" \\
  --duration-seconds 300 \\
  --query 'WebIdentityToken' --output text)`,
        },
        {
            language: 'bash',
            filename: 'exchange-token.sh',
            code: `curl -s -X POST "https://${ctx.tenantDomain}/auth/v1/token" \\
  -d "grant_type=urn:ietf:params:oauth:grant-type:token-exchange" \\
  -d "subject_token=$TOKEN" \\
  -d "subject_token_type=urn:ietf:params:oauth:token-type:jwt" \\
  -d "client_id=${ctx.clientId}"`,
        },
    ],
    securityGuidance:
        'Tokens issued by sts:GetWebIdentityToken represent an IAM principal (role or user). ' +
        'The sub claim is the IAM ARN -- this is a much stronger identity binding than CI providers. ' +
        'Prefer tag-based matching (principal_tags.environment, principal_tags.team) over ARN pattern ' +
        'wildcards. If using AWS Organizations, add the org_id constraint to prevent rogue standalone accounts.',
}

// ---------------------------------------------------------------------------
// Custom OIDC Provider
// ---------------------------------------------------------------------------

const CUSTOM_PRESET: WorkloadSourcePreset = {
    wellKnownProvider: WellKnownWorkloadProvider.CUSTOM,
    displayName: 'Custom OIDC provider',
    description: 'Connect any OIDC-compatible workload identity provider',
    iconKey: 'Custom',
    issuerUrl: null,
    issuerUrlDefault: '',
    issuerUrlEditable: true,
    providerDisplayName: '',
    fields: [],
    celTemplate: (): string => '',
    displayNameTemplate: (): string => '',
    successSnippets: (ctx): SuccessSnippet[] => [{
        language: 'bash',
        code: `curl -s -X POST "https://${ctx.tenantDomain}/auth/v1/token" \\
  -d "grant_type=urn:ietf:params:oauth:grant-type:token-exchange" \\
  -d "subject_token=<YOUR_JWT>" \\
  -d "subject_token_type=urn:ietf:params:oauth:token-type:jwt" \\
  -d "client_id=${ctx.clientId}"`,
    }],
    securityGuidance:
        'The CEL expression is evaluated against the JWT claims. It must return true for the token ' +
        'exchange to succeed. Always validate the subject (sub) claim and use additional claims for ' +
        'defense-in-depth scoping.',
}

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

export const WORKLOAD_SOURCE_PRESETS: WorkloadSourcePreset[] = [
    GITHUB_ACTIONS_PRESET,
    GITLAB_CI_PRESET,
    HCP_TERRAFORM_PRESET,
    AWS_IAM_OUTBOUND_PRESET,
    CUSTOM_PRESET,
]

/**
 * Lookup helper: given a provider's well_known_provider enum, get the preset.
 * Returns the CUSTOM preset for UNSPECIFIED (legacy providers).
 */
export const getPresetForProvider = (
    wkp: WellKnownWorkloadProvider,
): WorkloadSourcePreset => {
    const preset = WORKLOAD_SOURCE_PRESETS.find((p) => p.wellKnownProvider === wkp)
    return preset ?? CUSTOM_PRESET
}
